import * as React from 'react';

// The puzzle banner across the top of the review, in the game bar's place
// while a puzzle is on the board: the puzzle's place among the game's, what
// it asks, whose move it is, the game in the accent, and the ways on. Drawn
// from props only; the container in `index.tsx` turns the solver's state
// into these props (`banner.ts`) and the game bar yields its row to this.

// The puzzle piece, filled with big tabs (2026-10-14), the same glyph as the step bar's Puzzle button.
const PUZZLE = 'M72 104V72h40a24 24 0 1 1 32 0h40v40a24 24 0 1 0 0 40v40h-40a24 24 0 1 1-32 0H72v-40a24 24 0 1 1 0-40z';

export interface PuzzleBannerProps {
  /** "Puzzle 2 of 5 · Find the better move", "Solved · Nxe5". */
  title: string;
  /** "White to move · try 1", or after solving what the move achieves. */
  line: string;
  /** "EdBlackKnight vs mmarcin55 · Oct 9"; absent while the record loads. */
  game?: string;
  /** The puzzle is over: solved, or its solution shown. */
  done: boolean;
  /** A hint is offered: what it says when pressed. */
  onHint?: () => void;
  /** Plays the correct line on the board by itself; absent when not offered. */
  onShow?: () => void;
  /** Back to the puzzle's position for another go; absent when not offered. */
  onRetry?: () => void;
  /** Goes on to the next puzzle of the game; absent when this is the last. */
  onNext?: () => void;
  /** Ends the puzzle and returns to the game. */
  onStop: () => void;
}

export const PuzzleBanner: React.FC<PuzzleBannerProps> = ({ title, line, game, done, onHint, onShow, onRetry, onNext, onStop }) => (
  <header className={`puzzle-banner${done ? ' puzzle-banner-done' : ''}`} aria-live="polite" aria-label="Puzzle">
    <svg className="puzzle-banner-icon" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true">
      <path d={PUZZLE} />
    </svg>
    <div className="puzzle-banner-text">
      <h1 className="puzzle-banner-title">{title}</h1>
      <p className="puzzle-banner-line">
        {line}
        {game && (
          <>
            <span className="puzzle-banner-dot" aria-hidden="true">
              {' · '}
            </span>
            <span className="puzzle-banner-game">{game}</span>
          </>
        )}
      </p>
    </div>
    <div className="puzzle-banner-actions">
      {onHint && (
        <button type="button" className="btn btn-ghost" data-track="review.puzzle.hint" onClick={onHint}>
          Hint
        </button>
      )}
      {onShow && (
        <button type="button" className="btn btn-ghost" data-track="review.puzzle.solution" onClick={onShow} title="Play the correct line on the board">
          Show solution
        </button>
      )}
      {onRetry && (
        <button type="button" className="btn btn-ghost" data-track="review.puzzle.retry" onClick={onRetry}>
          Try again
        </button>
      )}
      {onNext && (
        <button type="button" className="btn btn-primary" data-track="review.puzzle.next" onClick={onNext}>
          Next puzzle <span aria-hidden="true">›</span>
        </button>
      )}
      <button type="button" className={`btn ${onNext ? 'btn-secondary' : 'btn-primary'}`} data-track="review.puzzle.back" onClick={onStop}>
        Back to the game
      </button>
    </div>
  </header>
);
